import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/ApiService';

// ── Types ─────────────────────────────────────────────────────────────────────
interface License {
  tenant_id: number; tenant_name: string; company_name: string;
  pbx_users: number; contact_centre: number; ai_agent: number; voice_bot: number; supervisor: number;
  notes: string; updated_at: string;
  used_pbx_users: number; used_contact_centre: number; used_ai_agent: number;
  used_voice_bot: number; used_supervisor: number;
}
interface UserRow {
  id: number; username: string; display_name?: string; tenant_id: number;
  is_active: number; role: string; user_type: string;
  cc_enabled: number; queue_enabled: number; supervisor_enabled: number;
  ai_agent_enabled: number; voice_bot_enabled: number;
  feature_recording: number; feature_contacts: number; feature_from_number: number;
  feature_recent_calls: number; feature_settings: number; feature_transfer: number;
  feature_queue_mode: number; feature_supervisor: number; feature_wallboard: number;
  feature_crm_popup: number; feature_ai_transcription: number;
  extension_number?: string;
}
interface LicType {
  key: 'pbx_users'|'contact_centre'|'ai_agent'|'voice_bot'|'supervisor';
  usedKey: 'used_pbx_users'|'used_contact_centre'|'used_ai_agent'|'used_voice_bot'|'used_supervisor';
  userField: keyof UserRow;
  label: string; icon: string; color: string; desc: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const LIC_TYPES: LicType[] = [
  { key:'pbx_users',      usedKey:'used_pbx_users',      userField:'is_active',         label:'PBX Users',      icon:'☎',  color:'#6366f1', desc:'Standard telephony' },
  { key:'contact_centre', usedKey:'used_contact_centre',  userField:'cc_enabled',        label:'Contact Centre', icon:'🎧', color:'#0ea5e9', desc:'Queue & agent features' },
  { key:'ai_agent',       usedKey:'used_ai_agent',        userField:'ai_agent_enabled',  label:'AI Agent',       icon:'🤖', color:'#8b5cf6', desc:'AI-assisted tools' },
  { key:'voice_bot',      usedKey:'used_voice_bot',       userField:'voice_bot_enabled', label:'Voice Bot',      icon:'🗣',  color:'#f59e0b', desc:'Automated voice bot' },
  { key:'supervisor',     usedKey:'used_supervisor',      userField:'supervisor_enabled',label:'Supervisor',     icon:'📡', color:'#10b981', desc:'Real-time monitoring' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const pct = (used: number, total: number) => total === 0 ? 0 : Math.min(100, Math.round(used / total * 100));
const barCol = (p: number, base: string) => p >= 100 ? '#ef4444' : p >= 80 ? '#f59e0b' : base;

// ── Root component (routes to super-admin or tenant-admin view) ───────────────
export default function LicensesPage({ user }: { user: any }) {
  const isSuperAdmin = user?.role === 'super_admin' || user?.is_admin;
  return isSuperAdmin
    ? <SuperAdminView />
    : <TenantAdminView user={user} />;
}

// ══════════════════════════════════════════════════════════════════════════════
//  SUPER ADMIN VIEW  — manage allocations across all tenants
// ══════════════════════════════════════════════════════════════════════════════
function SuperAdminView() {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading]   = useState(true);
  const [editTenant, setEdit]   = useState<License | null>(null);
  const [form, setForm]         = useState({ pbx_users:0, contact_centre:0, ai_agent:0, voice_bot:0, supervisor:0, notes:'' });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [search, setSearch]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api.getTenantLicenses(); setLicenses(Array.isArray(d) ? d : []); }
    catch { setError('Failed to load'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (l: License) => {
    setEdit(l);
    setForm({ pbx_users:l.pbx_users||0, contact_centre:l.contact_centre||0, ai_agent:l.ai_agent||0, voice_bot:l.voice_bot||0, supervisor:l.supervisor||0, notes:l.notes||'' });
    setError(''); setSuccess('');
  };
  const closeEdit = () => { setEdit(null); setError(''); setSuccess(''); };
  const save = async () => {
    if (!editTenant) return;
    setSaving(true); setError('');
    try { await api.updateTenantLicense(editTenant.tenant_id, form); setSuccess('Saved'); await load(); setTimeout(closeEdit, 800); }
    catch (e: any) { setError(e?.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  const totals = licenses.reduce((a, l) => {
    LIC_TYPES.forEach(t => { (a as any)[t.key] = ((a as any)[t.key]||0) + (Number((l as any)[t.key])||0); (a as any)[t.usedKey] = ((a as any)[t.usedKey]||0) + (Number((l as any)[t.usedKey])||0); });
    return a;
  }, {} as Record<string,number>);

  const filtered = licenses.filter(l => !search || l.tenant_name?.toLowerCase().includes(search.toLowerCase()) || l.company_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="admin-page-title">License Management</div>
      <div className="admin-page-subtitle">Manage license allocations per tenant. Super admin only.</div>

      {/* Platform summary */}
      <div style={Grid5}>
        {LIC_TYPES.map(lt => {
          const total = totals[lt.key]||0, used = totals[lt.usedKey]||0, p = pct(used,total);
          return (
            <div key={lt.key} style={{ ...Card, border:`1px solid ${lt.color}33` }}>
              <div style={{ fontSize:20 }}>{lt.icon}</div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginTop:4 }}>
                <span style={{ fontSize:22, fontWeight:700, color:lt.color }}>{used}</span>
                <span style={{ fontSize:12, color:'#64748b' }}>/ {total}</span>
              </div>
              <div style={{ fontSize:11, fontWeight:600, color:'#94a3b8', marginTop:2 }}>{lt.label}</div>
              <ProgressBar p={p} color={lt.color} />
              <div style={{ fontSize:10, color: p>=100?'#ef4444':p>=80?'#f59e0b':'#64748b', marginTop:2 }}>{p>=100?'⚠ Full':`${p}% used`}</div>
            </div>
          );
        })}
      </div>

      {/* Tenant table */}
      <div className="content-card" style={{ padding:0 }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center' }}>
          <span style={{ fontSize:14, fontWeight:600, color:'#e2e8f0' }}>Tenant Allocations</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tenants…" style={SearchInput} />
        </div>
        {loading ? <Loader /> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ background:'rgba(255,255,255,0.03)' }}>
                <th style={TH}>Tenant</th>
                {LIC_TYPES.map(lt => <th key={lt.key} style={{ ...TH, color:lt.color }}>{lt.icon} {lt.label}</th>)}
                <th style={TH}>Updated</th>
                <th style={TH}>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map((l, i) => (
                  <tr key={l.tenant_id} style={{ background:i%2===0?'transparent':'rgba(255,255,255,0.015)', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <td style={TD}><div style={{ fontWeight:600, color:'#e2e8f0' }}>{l.company_name||l.tenant_name}</div><div style={{ fontSize:11, color:'#64748b' }}>{l.tenant_name}</div></td>
                    {LIC_TYPES.map(lt => {
                      const total=Number((l as any)[lt.key])||0, used=Number((l as any)[lt.usedKey])||0, p=pct(used,total), over=total>0&&used>=total;
                      return (
                        <td key={lt.key} style={{ ...TD, textAlign:'center' }}>
                          <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:2 }}>
                            <span style={{ fontWeight:700, color:over?'#ef4444':used>0?lt.color:'#475569' }}>{used}<span style={{ fontWeight:400, color:'#64748b' }}>/{total}</span></span>
                            {total>0 && <ProgressBar p={p} color={lt.color} width={48} />}
                            {over && <span style={{ fontSize:9, color:'#ef4444', fontWeight:700 }}>FULL</span>}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ ...TD, fontSize:11, color:'#64748b' }}>{l.updated_at?new Date(l.updated_at).toLocaleDateString():'—'}</td>
                    <td style={TD}><button onClick={() => openEdit(l)} style={EditBtn}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit dialog */}
      {editTenant && (
        <div style={Overlay}><div style={Dialog}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18 }}>
            <div><div style={{ fontWeight:700, fontSize:16, color:'#e2e8f0' }}>Edit Licenses — {editTenant.company_name||editTenant.tenant_name}</div><div style={{ fontSize:12, color:'#64748b' }}>{editTenant.tenant_name}</div></div>
            <button onClick={closeEdit} style={XBtn}>✕</button>
          </div>
          {error   && <AlertBox color="red">{error}</AlertBox>}
          {success && <AlertBox color="green">{success}</AlertBox>}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            {LIC_TYPES.map(lt => {
              const used=Number((editTenant as any)[lt.usedKey])||0, total=Number((form as any)[lt.key])||0, p=pct(used,total), over=total>0&&used>total;
              return (
                <div key={lt.key} style={{ padding:12, borderRadius:8, border:`1px solid ${lt.color}33`, background:'rgba(255,255,255,0.03)' }}>
                  <label style={{ fontSize:11, fontWeight:700, color:lt.color, display:'block', marginBottom:4 }}>{lt.icon} {lt.label}</label>
                  <div style={{ fontSize:10, color:'#64748b', marginBottom:8 }}>{lt.desc}</div>
                  <input type="number" min="0" max="9999" value={(form as any)[lt.key]}
                    onChange={e => setForm(p => ({ ...p, [lt.key]: Math.max(0,Number(e.target.value)) }))}
                    style={NumInput} />
                  <div style={{ marginTop:5, display:'flex', alignItems:'center', gap:5 }}>
                    <ProgressBar p={p} color={lt.color} style={{ flex:1 }} />
                    <span style={{ fontSize:10, color:over?'#ef4444':'#64748b', fontWeight:over?700:400 }}>{used} used{over?' ⚠':''}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, fontWeight:600, color:'#94a3b8', display:'block', marginBottom:5 }}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes:e.target.value }))} rows={2}
              placeholder="Contract reference…" style={{ ...NumInput, resize:'vertical', boxSizing:'border-box' as const }} />
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={closeEdit} style={CancelBtn}>Cancel</button>
            <button onClick={save} disabled={saving} style={SaveBtn}>{saving?'Saving…':'Save'}</button>
          </div>
        </div></div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  TENANT ADMIN VIEW  — assign licenses to users within their tenant
// ══════════════════════════════════════════════════════════════════════════════
function TenantAdminView({ user }: { user: any }) {
  const [lic, setLic]       = useState<License | null>(null);
  const [users, setUsers]   = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string>(''); // "userId-field"
  const [flashErr, setFlashErr] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [licData, usersData] = await Promise.all([
        api.getTenantLicenseWithUsage(user.tenant_id),
        api.getAdminUsers(),
      ]);
      setLic(licData);
      setUsers(Array.isArray(usersData) ? usersData.filter((u: UserRow) => u.role !== 'super_admin') : []);
    } catch { }
    setLoading(false);
  }, [user.tenant_id]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const toggle = async (u: UserRow, field: keyof UserRow) => {
    const key = `${u.id}-${field}`;
    setToggling(key);
    setFlashErr(prev => ({ ...prev, [key]: '' }));

    const newVal = u[field] ? 0 : 1;
    const payload = { ...u, [field]: newVal };

    try {
      await api.updateAdminUser(u.id, payload);
      // Optimistic update + reload for usage counts
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, [field]: newVal } : x));
      // Refresh license usage
      const updated = await api.getTenantLicenseWithUsage(user.tenant_id);
      setLic(updated);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'License limit reached';
      setFlashErr(prev => ({ ...prev, [key]: msg }));
      setTimeout(() => setFlashErr(prev => ({ ...prev, [key]: '' })), 4000);
    }
    setToggling('');
  };

  const filtered = users.filter(u =>
    !search || u.username.toLowerCase().includes(search.toLowerCase()) || (u.display_name||'').toLowerCase().includes(search.toLowerCase())
  );

  // Per-type availability
  const avail = (lt: LicType) => {
    if (!lic) return { used:0, total:0, ok:true };
    const total = Number((lic as any)[lt.key])||0;
    const used  = Number((lic as any)[lt.usedKey])||0;
    return { used, total, ok: total===0 || used < total };
  };

  return (
    <div>
      <div className="admin-page-title">License Management</div>
      <div className="admin-page-subtitle">View your license pool and assign features to users.</div>

      {/* License pool summary (read-only) */}
      {lic && (
        <div style={Grid5}>
          {LIC_TYPES.map(lt => {
            const { used, total, ok } = avail(lt);
            const p = pct(used, total);
            return (
              <div key={lt.key} style={{ ...Card, border:`1px solid ${ok?lt.color+'33':'#ef444433'}` }}>
                <div style={{ fontSize:18 }}>{lt.icon}</div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginTop:3 }}>
                  <span style={{ fontSize:20, fontWeight:700, color:ok?lt.color:'#ef4444' }}>{used}</span>
                  <span style={{ fontSize:12, color:'#64748b' }}>/ {total||'—'}</span>
                </div>
                <div style={{ fontSize:11, fontWeight:600, color:'#94a3b8', marginTop:2 }}>{lt.label}</div>
                {total>0 && <ProgressBar p={p} color={lt.color} />}
                <div style={{ fontSize:10, color:!ok?'#ef4444':p>=80?'#f59e0b':'#64748b', marginTop:2, fontWeight:!ok?700:400 }}>
                  {total===0 ? 'Not allocated' : !ok ? '⚠ License full' : `${total-used} available`}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* User assignment table */}
      <div className="content-card" style={{ padding:0 }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:14, fontWeight:600, color:'#e2e8f0' }}>User License Assignment</span>
          <span style={{ fontSize:12, color:'#64748b' }}>Click a toggle to assign or revoke a license</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users…" style={SearchInput} />
        </div>

        {loading ? <Loader /> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ background:'rgba(255,255,255,0.03)' }}>
                <th style={TH}>User</th>
                {LIC_TYPES.map(lt => {
                  const { used, total, ok } = avail(lt);
                  return (
                    <th key={lt.key} style={{ ...TH, color: !ok ? '#ef4444' : lt.color }}>
                      <div>{lt.icon} {lt.label}</div>
                      {total > 0 && <div style={{ fontSize:9, fontWeight:400, color: !ok?'#ef4444':'#64748b', marginTop:2 }}>{used}/{total} used</div>}
                      {total === 0 && <div style={{ fontSize:9, color:'#64748b', marginTop:2 }}>Not allocated</div>}
                    </th>
                  );
                })}
              </tr></thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={LIC_TYPES.length + 1} style={{ padding:30, textAlign:'center', color:'#64748b' }}>No users found</td></tr>
                )}
                {filtered.map((u, i) => (
                  <tr key={u.id} style={{ background:i%2===0?'transparent':'rgba(255,255,255,0.015)', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <td style={TD}>
                      <div style={{ fontWeight:600, color:'#e2e8f0' }}>{u.display_name || u.username}</div>
                      {u.display_name && u.display_name !== u.username && <div style={{ fontSize:11, color:'#64748b' }}>{u.username}</div>}
                      {u.extension_number && <div style={{ fontSize:11, color:'#6366f1', fontWeight:600 }}>Ext {u.extension_number}</div>}
                      <span style={{ fontSize:10, padding:'1px 6px', borderRadius:99, background:'rgba(255,255,255,0.07)', color:'#94a3b8', marginTop:2, display:'inline-block' }}>{u.role}</span>
                    </td>
                    {LIC_TYPES.map(lt => {
                      const key = `${u.id}-${lt.userField}`;
                      const isOn = !!u[lt.userField];
                      const { ok, total } = avail(lt);
                      const busy = toggling === key;
                      const err  = flashErr[key];
                      const wouldExceed = !isOn && !ok && total > 0;

                      return (
                        <td key={lt.key} style={{ ...TD, textAlign:'center', verticalAlign:'top', paddingTop:14 }}>
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                            <button
                              onClick={() => !busy && !wouldExceed && toggle(u, lt.userField)}
                              disabled={busy || (wouldExceed)}
                              title={wouldExceed ? `No ${lt.label} licenses available` : isOn ? `Revoke ${lt.label}` : `Assign ${lt.label}`}
                              style={{
                                width: 44, height: 24, borderRadius: 12,
                                border: 'none', cursor: wouldExceed||busy ? 'not-allowed' : 'pointer',
                                background: busy ? '#64748b' : isOn ? lt.color : 'rgba(255,255,255,0.1)',
                                opacity: wouldExceed ? 0.4 : 1,
                                transition: 'background .2s',
                                display: 'flex', alignItems: 'center',
                                justifyContent: isOn ? 'flex-end' : 'flex-start',
                                padding: '0 3px',
                                position: 'relative',
                              }}
                            >
                              <div style={{ width:18, height:18, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }} />
                            </button>
                            <span style={{ fontSize:10, color: isOn ? lt.color : '#475569', fontWeight: isOn ? 700 : 400 }}>
                              {busy ? '…' : isOn ? 'On' : 'Off'}
                            </span>
                            {err && (
                              <div style={{ fontSize:9, color:'#ef4444', maxWidth:80, textAlign:'center', lineHeight:1.2, fontWeight:600 }}>
                                ⚠ Limit reached
                              </div>
                            )}
                            {wouldExceed && (
                              <div style={{ fontSize:9, color:'#f59e0b', maxWidth:80, textAlign:'center', lineHeight:1.2 }}>
                                No licenses
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ marginTop:16, padding:'12px 16px', borderRadius:8, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', fontSize:12, color:'#64748b' }}>
        <strong style={{ color:'#94a3b8' }}>How licenses work:</strong> Each toggle assigns or revokes a feature license for the user.
        Grayed-out toggles mean the license pool is full — contact your platform administrator to increase the allocation.
        Changes take effect immediately.
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────
function ProgressBar({ p, color, width, style: sx }: { p:number; color:string; width?:number; style?: React.CSSProperties }) {
  return (
    <div style={{ height:4, background:'rgba(255,255,255,0.08)', borderRadius:2, overflow:'hidden', width:width||'100%', marginTop:4, ...sx }}>
      <div style={{ height:'100%', width:`${p}%`, background:barCol(p,color), borderRadius:2, transition:'width .3s' }} />
    </div>
  );
}
function Loader() { return <div style={{ padding:40, textAlign:'center', color:'#64748b' }}>Loading…</div>; }
function AlertBox({ color, children }: { color:'red'|'green'; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom:12, padding:'9px 13px', borderRadius:7, fontSize:13,
      background: color==='red'?'rgba(239,68,68,0.15)':'rgba(16,185,129,0.15)',
      color: color==='red'?'#fca5a5':'#6ee7b7',
      border: `1px solid ${color==='red'?'rgba(239,68,68,0.3)':'rgba(16,185,129,0.3)'}` }}>
      {children}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const Grid5: React.CSSProperties = { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))', gap:12, marginBottom:24 };
const Card: React.CSSProperties  = { background:'rgba(255,255,255,0.04)', borderRadius:10, padding:14 };
const TH: React.CSSProperties    = { padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase' as const, letterSpacing:'0.5px', whiteSpace:'nowrap' as const };
const TD: React.CSSProperties    = { padding:'11px 14px', verticalAlign:'middle' };
const Overlay: React.CSSProperties = { position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 };
const Dialog: React.CSSProperties  = { background:'#1e2235', border:'1px solid rgba(255,255,255,0.1)', borderRadius:14, padding:26, width:560, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' as const, boxShadow:'0 20px 60px rgba(0,0,0,0.5)' };
const SearchInput: React.CSSProperties = { marginLeft:'auto', padding:'5px 11px', borderRadius:6, border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.05)', color:'#e2e8f0', fontSize:13, outline:'none', width:200 };
const NumInput: React.CSSProperties    = { width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.05)', color:'#e2e8f0', fontSize:14, fontWeight:600, outline:'none', boxSizing:'border-box' as const };
const EditBtn: React.CSSProperties     = { padding:'5px 13px', borderRadius:6, border:'1px solid #6366f1', background:'rgba(99,102,241,0.15)', color:'#a5b4fc', cursor:'pointer', fontSize:12, fontWeight:600 };
const XBtn: React.CSSProperties        = { background:'none', border:'none', color:'#64748b', fontSize:18, cursor:'pointer' };
const CancelBtn: React.CSSProperties   = { padding:'7px 16px', borderRadius:7, border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#94a3b8', fontWeight:600, fontSize:13, cursor:'pointer' };
const SaveBtn: React.CSSProperties     = { padding:'7px 20px', borderRadius:7, border:'none', background:'#6366f1', color:'#fff', fontWeight:600, fontSize:13, cursor:'pointer' };
